/* ==========================================================================
   <x-slider>: touch slider built on native scroll-snap (no library).
   Markup:
     <x-slider data-autoplay="5000" data-loop>
       <ul class="slider__track" role="list">…<li class="slider__slide">…</li></ul>
       <button data-slider-prev>…</button> <button data-slider-next>…</button>
       <div class="slider__dots"></div>   (optional, filled automatically)
     </x-slider>
   Works in RTL: scrollLeft is negative there, so all math uses Math.abs.
   ========================================================================== */
if (!customElements.get('x-slider')) {
  customElements.define(
    'x-slider',
    class XSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.slider__track');
        if (!this.track) return;
        this.slides = [...this.track.children];
        this.prevButton = this.querySelector('[data-slider-prev]');
        this.nextButton = this.querySelector('[data-slider-next]');
        this.dotsContainer = this.querySelector('.slider__dots');
        this.loop = this.hasAttribute('data-loop');

        this.prevButton?.addEventListener('click', () => this.go(-1));
        this.nextButton?.addEventListener('click', () => this.go(1));
        this.track.addEventListener('scroll', window.theme.utils.throttle(() => this.update(), 100), { passive: true });
        this.track.addEventListener('keydown', (event) => this.onKeydown(event));

        this.resizeObserver = new ResizeObserver(() => {
          this.buildDots();
          this.update();
        });
        this.resizeObserver.observe(this.track);

        // Flag slides currently in view without measuring layout on every scroll
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => entry.target.toggleAttribute('data-visible', entry.intersectionRatio > 0.5));
            this.dispatchEvent(new CustomEvent('slider:visible'));
          },
          { root: this.track, threshold: [0, 0.5, 1] }
        );
        this.slides.forEach((slide) => this.visibilityObserver.observe(slide));

        this.setupAutoplay();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        clearInterval(this.timer);
      }

      get rtl() {
        return getComputedStyle(this.track).direction === 'rtl';
      }

      get position() {
        return Math.abs(this.track.scrollLeft);
      }

      get maxScroll() {
        return this.track.scrollWidth - this.track.clientWidth;
      }

      get pageSize() {
        const slide = this.slides[0];
        if (!slide) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const perPage = Math.max(1, Math.round((this.track.clientWidth + gap) / (slide.offsetWidth + gap)));
        return perPage * (slide.offsetWidth + gap);
      }

      scrollToPosition(position) {
        const left = this.rtl ? -position : position;
        this.track.scrollTo({ left, behavior: window.theme.utils.prefersReducedMotion() ? 'auto' : 'smooth' });
      }

      go(direction) {
        let target = this.position + direction * this.pageSize;
        if (this.loop) {
          if (direction > 0 && this.position >= this.maxScroll - 2) target = 0;
          if (direction < 0 && this.position <= 2) target = this.maxScroll;
        }
        this.scrollToPosition(Math.max(0, Math.min(target, this.maxScroll)));
      }

      goToPage(index) {
        this.scrollToPosition(Math.min(index * this.pageSize, this.maxScroll));
      }

      onKeydown(event) {
        if (event.target !== this.track) return;
        const forward = this.rtl ? 'ArrowLeft' : 'ArrowRight';
        const back = this.rtl ? 'ArrowRight' : 'ArrowLeft';
        if (event.key === forward) {
          event.preventDefault();
          this.go(1);
        } else if (event.key === back) {
          event.preventDefault();
          this.go(-1);
        }
      }

      buildDots() {
        if (!this.dotsContainer) return;
        const pages = this.maxScroll > 1 ? Math.ceil(this.maxScroll / this.pageSize) + 1 : 1;
        if (this.dotsContainer.childElementCount === pages) return;
        this.dotsContainer.innerHTML = '';
        this.dotsContainer.hidden = pages < 2;
        const label = this.dataset.dotLabel || 'Slide';
        for (let i = 0; i < pages; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'slider__dot';
          dot.setAttribute('aria-label', `${label} ${i + 1}`);
          dot.addEventListener('click', () => this.goToPage(i));
          this.dotsContainer.appendChild(dot);
        }
      }

      update() {
        const atStart = this.position <= 2;
        const atEnd = this.position >= this.maxScroll - 2;
        if (this.prevButton) this.prevButton.disabled = !this.loop && atStart;
        if (this.nextButton) this.nextButton.disabled = !this.loop && atEnd;
        this.toggleAttribute('data-scrollable', this.maxScroll > 2);

        if (this.dotsContainer) {
          const dots = [...this.dotsContainer.children];
          const current = atEnd ? dots.length - 1 : Math.round(this.position / this.pageSize);
          dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
        }
      }

      setupAutoplay() {
        const delay = Number(this.dataset.autoplay);
        if (!delay || window.theme.utils.prefersReducedMotion()) return;
        const start = () => {
          clearInterval(this.timer);
          this.timer = setInterval(() => {
            if (this.position >= this.maxScroll - 2) this.scrollToPosition(0);
            else this.go(1);
          }, delay);
        };
        const stop = () => clearInterval(this.timer);
        this.addEventListener('mouseenter', stop);
        this.addEventListener('mouseleave', start);
        this.addEventListener('focusin', stop);
        this.addEventListener('focusout', start);
        this.addEventListener('pointerdown', stop);
        document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
        start();
      }
    }
  );
}
